/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useContext, useEffect, useState } from 'react';

import { Button, Dialog, InlineNotification, StandaloneCheckbox, Typography2 } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { ProfileNotificationContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileNotificationProvider';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type PrivacyToggleDialogProps = {
  isOpen: boolean;
  onClose: (isShareToPublic?: boolean | null) => void;
};

const styles = {
  checkbox: css`
    .cds-checkboxAndRadio-label {
      margin-bottom: 0;
    }

    .cds-checkboxAndRadio-labelContent {
      font-weight: bold;
    }
  `,
  marginTop: css`
    margin-top: var(--cds-spacing-200);
  `,
  talentPipelineWarningNotification: css`
    margin-top: var(--cds-spacing-400);
  `,
  dialogHeadingText: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  checkBoxHeading: css`
    margin-bottom: var(--cds-spacing-50);
  `,
};

const PrivacySettingsDialog = ({ isOpen, onClose }: PrivacyToggleDialogProps) => {
  const { profileState = {}, updateLearnerProfile, restrictHiringSolutions } = useContext(ProfileContext);
  const { sendNotification } = useContext(ProfileNotificationContext);
  const { HeadingGroup, Content, Actions } = Dialog;
  const [showToPublic, setShowToPublic] = useState<boolean>(profileState.showToPublic ?? false);
  const [includedInTalentPipeline, setIncludedInTalentPipeline] = useState<boolean>(
    profileState.includedInTalentPipeline ?? false
  );
  const [initialIncludedInTalentPipeline, setInitialIncludedInTalentPipeline] = useState<boolean | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setShowToPublic(profileState.showToPublic ?? false);
    setIncludedInTalentPipeline(profileState.includedInTalentPipeline ?? false);
    if (!initialIncludedInTalentPipeline)
      setInitialIncludedInTalentPipeline(profileState.includedInTalentPipeline ?? undefined);
  }, [profileState, initialIncludedInTalentPipeline, isOpen]);

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      await updateLearnerProfile({
        showToPublic,
        includedInTalentPipeline,
      });
      onClose(showToPublic);
    } catch {
      setIsSubmitting(false);
      sendNotification({
        severity: 'error',
        message: _t('Something went wrong, please try again later.'),
        title: _t('Profile visibility and privacy settings'),
      });
    }
  };

  return (
    <Dialog aria-describedby="dialog-content" open={isOpen} variant="standard" onClose={() => onClose()}>
      <HeadingGroup>{_t('Profile visibility and privacy settings')}</HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          requireFullyVisible={false}
          // not tracked in eventing v3
          trackingName="profile_privacy_settings_dialog"
        >
          <Typography2 component="p" variant="bodyPrimary" css={styles.dialogHeadingText}>
            {_t(
              'Sharing your profile is a great way to stand out by showcasing your skills and accomplishments on Coursera.'
            )}
          </Typography2>

          <Typography2 component="h3" css={styles.checkBoxHeading} variant="subtitleMedium">
            {_t('Who do you want to see your profile?')}
          </Typography2>
          <StandaloneCheckbox
            css={styles.checkbox}
            label={_t('Anyone with my link')}
            aria-label={_t('Anyone with my link')}
            supportText={
              <Typography2 component="p" variant="bodyPrimary">
                {_t(
                  'Anyone who has the link to your profile can view it (but it won’t appear as a result on search engines).'
                )}
              </Typography2>
            }
            defaultChecked={showToPublic}
            checked={showToPublic}
            onChange={(_, isChecked) => setShowToPublic(isChecked)}
            data-testid="show-to-public-checkbox"
          />
          {!restrictHiringSolutions && initialIncludedInTalentPipeline && (
            <div>
              <StandaloneCheckbox
                css={styles.checkbox}
                aria-label={_t('Recruiters using Coursera Hiring Solutions')}
                label={_t('Recruiters using Coursera Hiring Solutions')}
                supportText={
                  <Typography2 component="p" variant="bodyPrimary">
                    {_t(
                      'Recruiters using Coursera Hiring Solutions will see your profile in their list of available talent.'
                    )}
                  </Typography2>
                }
                defaultChecked={includedInTalentPipeline}
                checked={includedInTalentPipeline}
                onChange={(_, isChecked) => setIncludedInTalentPipeline(isChecked)}
              />
              {initialIncludedInTalentPipeline && !includedInTalentPipeline && (
                <InlineNotification
                  css={styles.talentPipelineWarningNotification}
                  severity="warning"
                  title={_t('These changes mean some recruiters won’t see your profile')}
                >
                  {_t(
                    'Are you sure you want to make these changes? If you save them, your profile won’t be visible for recruiters using Coursera Hiring Solutions, and they won’t be able to invite you to apply for open positions.'
                  )}
                </InlineNotification>
              )}
            </div>
          )}
        </TrackedDiv>
      </Content>
      <Actions>
        <Button
          variant="primary"
          onClick={onSubmit}
          iconPosition="before"
          loading={isSubmitting}
          component={TrackedButton}
          requireFullyVisible={false}
          withVisibilityTracking={true}
          // not tracked in eventing v3
          trackingName="profile_save_privacy_settings"
        >
          {_t('Done')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default PrivacySettingsDialog;
